import { useCallback, useEffect, useRef, useState } from "react";
import { authApi } from "../../../api";

export interface UseVerifyResetCodeArgs {
    email: string;
    onVerified: (code: string) => void;
}

export interface UseVerifyResetCodeReturn {
    code: string;
    setCode: (v: string) => void;
    verifying: boolean;
    verifyStatus: "idle" | "error" | "success";
    error: string | null;
    canContinue: boolean;

    handleVerify: () => Promise<void>;

    /* resend */
    resending: boolean;
    secondsLeft: number;
    canResend: boolean;
    handleResend: () => Promise<void>;
}

export function useVerifyResetCode(
    args: UseVerifyResetCodeArgs,
): UseVerifyResetCodeReturn {
    const { email, onVerified } = args;

    const [code, setCode] = useState("");
    const [verifying, setVerifying] = useState(false);
    const [verifyStatus, setVerifyStatus] = useState<"idle" | "error" | "success">("idle");
    const [error, setError] = useState<string | null>(null);
    const verifiedRef = useRef(false);

    /* resend timer */
    const [resending, setResending] = useState(false);
    const [secondsLeft, setSecondsLeft] = useState(60);
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const canResend = secondsLeft === 0 && !resending;
    const canContinue = code.length === 6 && !verifying && verifyStatus !== "success";

    /* start countdown */
    const startCountdown = useCallback(() => {
        setSecondsLeft(60);
        if (intervalRef.current) clearInterval(intervalRef.current);
        intervalRef.current = setInterval(() => {
            setSecondsLeft((s) => (s <= 1 ? 0 : s - 1));
        }, 1000);
    }, []);

    // Start countdown on mount (code was already sent from forgot-password)
    useEffect(() => {
        startCountdown();
        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
        };
    }, [startCountdown]);

    // Reset status when code changes
    useEffect(() => {
        if (verifyStatus !== "idle") {
            setVerifyStatus("idle");
            setError(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [code]);

    /* verify on button press — fires only once */
    const handleVerify = useCallback(async () => {
        if (code.length !== 6 || verifying || verifiedRef.current) return;

        setVerifying(true);
        setError(null);
        try {
            const res = await authApi.verifyResetCode(email, code);

            if (res.verified) {
                verifiedRef.current = true;
                setVerifyStatus("success");
                // small delay so user sees the green glow
                setTimeout(() => {
                    onVerified(code);
                }, 600);
            } else {
                setVerifyStatus("error");
                setError("Invalid code.");
            }
        } catch (e: unknown) {
            setVerifyStatus("error");
            const err = e as { response?: { data?: { detail?: string } }; message?: string };
            setError(
                err?.response?.data?.detail ?? err?.message ?? "Verification failed.",
            );
        } finally {
            setVerifying(false);
        }
    }, [code, email, onVerified, verifying]);

    /* resend */
    const handleResend = useCallback(async () => {
        if (!canResend) return;
        setResending(true);
        setError(null);
        setVerifyStatus("idle");
        setCode("");
        verifiedRef.current = false;

        try {
            await authApi.forgotPassword(email);
            startCountdown();
        } catch (e: unknown) {
            const err = e as { response?: { data?: { detail?: string } } };
            setError(
                err?.response?.data?.detail ?? "Could not resend code.",
            );
        } finally {
            setResending(false);
        }
    }, [canResend, email, startCountdown]);

    return {
        code,
        setCode,
        verifying,
        verifyStatus,
        error,
        canContinue,
        handleVerify,
        resending,
        secondsLeft,
        canResend,
        handleResend,
    };
}
